import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  Clock, 
  TrendingUp, 
  Award, 
  Users, 
  BookOpen,
  CheckCircle,
  Star,
  Calendar,
  Video,
  GraduationCap,
  Zap,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BecomeTutor = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    qualification: '',
    subjects: '',
    experience: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${API_URL}/api/tutor-application/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          qualification: '',
          subjects: '',
          experience: '',
          message: ''
        });
      } else {
        const error = await response.json();
        setSubmitStatus('error');
        console.error('Submission error:', error);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: DollarSign,
      title: 'Paid Per Hour',
      description: 'Earn competitive hourly rates with potential to earn up to ₹40,000 per month based on your availability',
      color: 'from-green-400 to-emerald-500'
    },
    {
      icon: Clock,
      title: 'Flexible Schedule',
      description: 'Set your own availability and teach at times that work for you',
      color: 'from-blue-400 to-cyan-500'
    },
    {
      icon: TrendingUp,
      title: 'Career Growth',
      description: 'Access professional development resources and advance your teaching career',
      color: 'from-purple-400 to-pink-500'
    },
    {
      icon: Award,
      title: 'Recognition & Rewards',
      description: 'Get featured as top tutor, earn bonuses, and receive student appreciation',
      color: 'from-yellow-400 to-orange-500'
    },
    {
      icon: Users,
      title: 'Supportive Community',
      description: 'Join a network of 100+ expert tutors and share best practices',
      color: 'from-red-400 to-pink-500'
    },
    {
      icon: Video,
      title: 'Advanced Tools',
      description: 'Use our state-of-the-art virtual classroom with interactive whiteboard',
      color: 'from-indigo-400 to-purple-500'
    }
  ];

  const requirements = [
    'Bachelor\'s degree in relevant subject (Master\'s preferred)',
    'Minimum 2 years of teaching experience',
    'Strong communication skills in English and Hindi',
    'Passion for teaching and student success',
    'Reliable internet connection and quiet teaching space',
    'Availability for at least 10 hours per week'
  ];

  const steps = [
    {
      number: '01',
      title: 'Apply Online',
      description: 'Fill out our simple application form with your qualifications'
    },
    {
      number: '02',
      title: 'Interview & Demo',
      description: 'Showcase your teaching skills in a short demo session'
    },
    {
      number: '03',
      title: 'Mandatory Training',
      description: 'Complete our paid FourQ Method training program and pass the assessment'
    },
    {
      number: '04',
      title: 'Start Teaching',
      description: 'Get matched with students and begin earning'
    }
  ];

  const tutorTestimonials = [
    {
      name: 'Dr. Priya Sharma',
      subject: 'Mathematics Teacher',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2',
      text: 'Teaching with FourQ has been incredibly rewarding. The flexible schedule allows me to balance my work perfectly, and the students are genuinely motivated to learn.',
      rating: 5
    },
    {
      name: 'Rajesh Kumar',
      subject: 'Science Teacher',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d',
      text: 'The FourQ Method really works! I\'ve seen remarkable improvement in my students. Plus, the platform makes teaching online so easy and interactive.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-cyan-600 via-blue-600 to-teal-600 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <motion.div
            className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              x: [0, 50, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
            }}
          />
        </div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl lg:text-6xl font-bold mb-6">
                Inspire the Next Generation of Learners
              </h1>
              <p className="text-xl text-cyan-100 mb-8">
                Join India's fastest-growing online tutoring platform and make a real difference in students' lives while building a flexible, rewarding career.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button 
                  size="lg" 
                  className="bg-white text-cyan-600 hover:bg-gray-50 px-8 py-6 text-lg rounded-xl shadow-xl hover:scale-105 transition-all duration-300"
                  onClick={() => document.getElementById('application-form').scrollIntoView({ behavior: 'smooth' })}
                  data-testid="become-tutor-apply-btn"
                >
                  Apply Now
                  <Zap className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl backdrop-blur-sm transition-all duration-300"
                >
                  Learn More
                </Button>
              </div>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
            >
              {[
                { number: '100+', label: 'Expert Tutors' },
                { number: 'Per Hour', label: 'Payment Model' },
                { number: '1,000+', label: 'Students Taught' },
                { number: '4.9/5', label: 'Tutor Rating' }
              ].map((stat, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <div className="text-3xl font-bold">{stat.number}</div>
                  <div className="text-sm text-cyan-100">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Earning Potential Banner */}
      <section className="py-8 bg-gradient-to-r from-green-500 to-emerald-600">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-white text-center">
            <DollarSign className="w-10 h-10" />
            <div>
              <p className="text-2xl font-bold">Earn Up To ₹40,000 Per Month</p>
              <p className="text-green-100">Paid per hour • Flexible hours • Weekly payouts</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why Teach with <span className="text-cyan-600">FourQ Learn</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join thousands of passionate educators who are transforming education
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${benefit.color} rounded-2xl flex items-center justify-center mb-6`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                What We're Looking For
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We seek passionate educators who are committed to student success and eager to use innovative teaching methods.
              </p>
              <div className="space-y-4">
                {requirements.map((requirement, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm"
                  >
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{requirement}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f"
                alt="Teaching Excellence"
                className="rounded-3xl shadow-2xl w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Top-Rated</div>
                    <div className="text-sm text-gray-600">Teaching Platform</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Simple <span className="text-cyan-600">Application Process</span>
            </h2>
            <p className="text-xl text-gray-600">Get started in just 4 easy steps</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 h-full hover:shadow-lg transition-shadow">
                  <div className="text-6xl font-bold text-cyan-200 mb-4">{step.number}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-cyan-300" />
                )}
              </motion.div>
            ))}
          </div>

          {/* Training Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 max-w-4xl mx-auto"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Mandatory Paid Training Program</h3>
                <p className="text-gray-700">
                  Selected candidates are required to complete a <strong>mandatory paid training program</strong> on our FourQ teaching methodology. 
                  After successful completion and passing the assessment test, tutors can start teaching on the platform. 
                  This ensures all our tutors maintain the highest teaching standards and deliver consistent learning experiences.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tutor Testimonials */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-cyan-50">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our <span className="text-cyan-600">Tutors Say</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {tutorTestimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-cyan-600">{testimonial.subject}</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-600 italic">"{testimonial.text}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Ready to <span className="text-cyan-600">Get Started</span>?
              </h2>
              <p className="text-xl text-gray-600">
                Fill out the form below and we'll get back to you within 24 hours
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-8 lg:p-12 shadow-xl"
            >
              {submitStatus === 'success' && (
                <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-xl flex items-center gap-3">
                  <CheckCircle className="w-5 h-5" />
                  <span>Thank you for your application! We will contact you within 24 hours.</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5" />
                  <span>There was an error submitting your application. Please try again.</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <Input 
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your name"
                      required
                      className="w-full"
                      data-testid="tutor-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      required
                      className="w-full"
                      data-testid="tutor-email-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <Input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 98765 43210"
                      required
                      className="w-full"
                      data-testid="tutor-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Highest Qualification *
                    </label>
                    <Input 
                      type="text"
                      name="qualification"
                      value={formData.qualification}
                      onChange={handleInputChange}
                      placeholder="e.g., M.Sc. Mathematics"
                      required
                      className="w-full"
                      data-testid="tutor-qualification-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Subjects You Can Teach *
                  </label>
                  <Input 
                    type="text"
                    name="subjects"
                    value={formData.subjects}
                    onChange={handleInputChange}
                    placeholder="e.g., Math, Physics, Chemistry"
                    required
                    className="w-full"
                    data-testid="tutor-subjects-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Teaching Experience (Years) *
                  </label>
                  <Input 
                    type="number"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    placeholder="e.g., 5"
                    required
                    className="w-full"
                    data-testid="tutor-experience-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Why do you want to teach with FourQ? *
                  </label>
                  <Textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your teaching philosophy and why you'd be a great fit..."
                    required
                    rows={5}
                    className="w-full"
                    data-testid="tutor-message-input"
                  />
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                  data-testid="tutor-submit-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  {!isSubmitting && <CheckCircle className="ml-2 h-5 w-5" />}
                </Button>

                <p className="text-sm text-gray-600 text-center">
                  By submitting this form, you agree to our Terms of Service and Privacy Policy
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BecomeTutor;
